/**
 * BeadSystem.js — Shared kandi bead component library
 *
 * Exports: CLIP_PATHS, BEAD_COLORS, BEAD_SHAPE_TYPES,
 *          FLOAT_SHAPE_CYCLE, BeadShape, KandiBeadImage
 *
 * Used by: LandingPage, UserDashboard (collection + edit modal)
 */

import React from "react";

// Photorealistic bead renders (generated via scripts/generate-beads.mjs)
import beadHotpink  from "../assets/beads/bead-hotpink.png";
import beadCyan     from "../assets/beads/bead-cyan.png";
import beadPurple   from "../assets/beads/bead-purple.png";
import beadGold     from "../assets/beads/bead-gold.png";
import beadLime     from "../assets/beads/bead-lime.png";
import beadOrange   from "../assets/beads/bead-orange.png";
import beadWhite    from "../assets/beads/bead-white.png";
import beadBlue     from "../assets/beads/bead-blue.png";
import beadStarPink from "../assets/beads/bead-star-pink.png";
import beadHeartRed from "../assets/beads/bead-heart-red.png";
import beadDiamond  from "../assets/beads/bead-diamond.png";
import beadFlame     from "../assets/beads/bead-flame.png";
import beadTeddy     from "../assets/beads/bead-teddy.png";
import beadCandy     from "../assets/beads/bead-candy.png";
import beadLightning from "../assets/beads/bead-lightning.png";
import beadMushroom  from "../assets/beads/bead-mushroom.png";
import beadMoon      from "../assets/beads/bead-moon.png";
import beadButterfly from "../assets/beads/bead-butterfly.png";
import beadSkull     from "../assets/beads/bead-skull.png";
import beadAlien     from "../assets/beads/bead-alien.png";

// ─── Clip-path definitions for non-round bead shapes ─────────────────────────
export const CLIP_PATHS = {
  star:    "polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)",
  heart:   "polygon(50% 25%,75% 0%,100% 30%,85% 65%,50% 100%,15% 65%,0% 30%,25% 0%)",
  diamond: "polygon(50% 0%,100% 50%,50% 100%,0% 50%)",
  hex:     "polygon(25% 0%,75% 0%,100% 50%,75% 100%,25% 100%,0% 50%)",
  flower:  "polygon(50% 0%,60% 20%,85% 10%,75% 35%,100% 50%,75% 65%,85% 90%,60% 80%,50% 100%,40% 80%,15% 90%,25% 65%,0% 50%,25% 35%,15% 10%,40% 20%)",
};

// ─── Default neon bead palette ────────────────────────────────────────────────
export const BEAD_COLORS = [
  "#ff3cac", "#b44fff", "#00f5ff", "#ffd700",
  "#ff6b6b", "#4ecdc4", "#54a0ff", "#5f27cd",
  "#ff9ff3", "#ffeaa7", "#00cec9", "#fd79a8",
];

// ─── All available shape types (for pickers) ─────────────────────────────────
export const BEAD_SHAPE_TYPES = [
  "pony", "star", "heart", "cube", "diamond", "hex", "flower",
];

// ─── Repeating cycle for floating/ambient bead animations ────────────────────
export const FLOAT_SHAPE_CYCLE = [
  "pony", "star", "pony", "heart", "cube", "pony",
  "letter-P", "pony", "diamond", "letter-L", "pony", "hex",
  "pony", "star", "letter-U", "pony", "heart", "letter-R",
  "flower", "pony",
];

// ─────────────────────────────────────────────────────────────────────────────
/**
 * BeadShape — renders one kandi bead in any of 8 authentic bead styles:
 *   pony (round) · star · heart · cube (square) · diamond · hex · flower · letter-X
 *
 * Props:
 *   shapeType  string   one of BEAD_SHAPE_TYPES or "letter-X" (any single character)
 *   color      string   hex color
 *   size       number   px dimension (default 36)
 *   className  string   optional CSS class (e.g. "animate-float-bead")
 */
export function BeadShape({ shapeType = "pony", color, size = 36, className = "" }) {
  const isLetter = shapeType.startsWith("letter-");
  const letter   = isLetter ? shapeType.split("-")[1] : null;
  const clipKey  = isLetter ? null : shapeType;
  const clipPath = CLIP_PATHS[clipKey] || null;

  const base = {
    width:           size,
    height:          size,
    flexShrink:      0,
    background:      `radial-gradient(circle at 38% 32%, ${color}ff, ${color}88 55%, ${color}55)`,
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
  };

  let style;

  if (clipPath) {
    // Clip-path shapes: drop-shadow respects the clip boundary; box-shadow won't
    style = {
      ...base,
      clipPath,
      filter: `drop-shadow(0 3px 8px ${color}90) drop-shadow(0 0 4px ${color}60)`,
    };
  } else if (shapeType === "cube") {
    style = {
      ...base,
      borderRadius: "18%",
      boxShadow:    `0 4px 14px ${color}66, inset 0 2px 4px rgba(255,255,255,0.45)`,
      border:       "1.5px solid rgba(255,255,255,0.25)",
    };
  } else if (isLetter) {
    style = {
      ...base,
      borderRadius: "24%",
      boxShadow:    `0 4px 14px ${color}66, inset 0 2px 4px rgba(255,255,255,0.45)`,
      border:       "1.5px solid rgba(255,255,255,0.25)",
      fontSize:     Math.round(size * 0.48),
      fontWeight:   900,
      color:        "rgba(255,255,255,0.95)",
      textShadow:   "0 1px 3px rgba(0,0,0,0.55)",
      fontFamily:   "system-ui, sans-serif",
      lineHeight:   1,
    };
  } else {
    // pony — classic round
    style = {
      ...base,
      borderRadius: "50%",
      boxShadow:    `0 4px 14px ${color}66, 0 0 8px ${color}44, inset 0 2px 4px rgba(255,255,255,0.45)`,
      border:       "1.5px solid rgba(255,255,255,0.25)",
    };
  }

  return (
    <div style={style} className={className}>
      {letter}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * KandiBeadImage — renders a bead using the photorealistic generated PNGs.
 *
 * Round (pony) beads pick the image whose color is nearest the bead's hex;
 * star / heart / diamond map to their dedicated renders. Shapes with no photo
 * equivalent (cube, hex, flower, letter-X) fall back to the CSS BeadShape so
 * every bead still renders.
 */

const PONY_IMAGE_PALETTE = [
  { rgb: [255, 20, 147],  img: beadHotpink },
  { rgb: [0, 245, 255],   img: beadCyan    },
  { rgb: [157, 78, 221],  img: beadPurple  },
  { rgb: [255, 215, 0],   img: beadGold    },
  { rgb: [173, 255, 47],  img: beadLime    },
  { rgb: [255, 165, 0],   img: beadOrange  },
  { rgb: [245, 245, 255], img: beadWhite   },
  { rgb: [84, 160, 255],  img: beadBlue    },
];

const SHAPE_IMAGES = {
  star:      beadStarPink,
  heart:     beadHeartRed,
  diamond:   beadDiamond,
  flame:     beadFlame,
  teddy:     beadTeddy,
  candy:     beadCandy,
  lightning: beadLightning,
  mushroom:  beadMushroom,
  moon:      beadMoon,
  butterfly: beadButterfly,
  skull:     beadSkull,
  alien:     beadAlien,
};

/**
 * BEAD_CATALOG — every bead a user can string onto a kandi, each backed by a
 * generated photorealistic render. `color` is the representative hex stored
 * alongside the shape (used for glows, journey-map colors, letter beads, and
 * as the nearest-match key for the pony renders).
 */
export const BEAD_CATALOG = [
  { shape: "pony",      color: "#FF1493", name: "Hot Pink"  },
  { shape: "pony",      color: "#00F5FF", name: "Cyan"      },
  { shape: "pony",      color: "#9D4EDD", name: "Purple"    },
  { shape: "pony",      color: "#FFD700", name: "Gold"      },
  { shape: "pony",      color: "#ADFF2F", name: "Lime"      },
  { shape: "pony",      color: "#FFA500", name: "Orange"    },
  { shape: "pony",      color: "#F5F5FF", name: "White"     },
  { shape: "pony",      color: "#54A0FF", name: "Blue"      },
  { shape: "star",      color: "#FF9FF3", name: "Pink Star" },
  { shape: "heart",     color: "#FF4444", name: "Red Heart" },
  { shape: "diamond",   color: "#E0E7FF", name: "Diamond"   },
  { shape: "flame",     color: "#FF4500", name: "Flame"     },
  { shape: "teddy",     color: "#A0724F", name: "Teddy"     },
  { shape: "candy",     color: "#FF5A5A", name: "Candy"     },
  { shape: "lightning", color: "#FFFF33", name: "Lightning" },
  { shape: "mushroom",  color: "#FF3B3B", name: "Mushroom"  },
  { shape: "moon",      color: "#C8D8F0", name: "Moon"      },
  { shape: "butterfly", color: "#9D6BFF", name: "Butterfly" },
  { shape: "skull",     color: "#F0F0F0", name: "Skull"     },
  { shape: "alien",     color: "#39FF14", name: "Alien"     },
];

function hexToRgb(hex = "") {
  const m = hex.replace("#", "");
  if (m.length !== 6) return null;
  const n = parseInt(m, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function nearestPonyImage(color) {
  const rgb = hexToRgb(color);
  if (!rgb) return PONY_IMAGE_PALETTE[2].img; // default purple
  let best = PONY_IMAGE_PALETTE[0], bestD = Infinity;
  for (const entry of PONY_IMAGE_PALETTE) {
    const d =
      (rgb[0] - entry.rgb[0]) ** 2 +
      (rgb[1] - entry.rgb[1]) ** 2 +
      (rgb[2] - entry.rgb[2]) ** 2;
    if (d < bestD) { bestD = d; best = entry; }
  }
  return best.img;
}

export function KandiBeadImage({ shapeType = "pony", color, size = 36, className = "" }) {
  const src = SHAPE_IMAGES[shapeType] || (shapeType === "pony" ? nearestPonyImage(color) : null);

  if (!src) {
    return <BeadShape shapeType={shapeType} color={color} size={size} className={className} />;
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      style={{
        width: size, height: size,
        objectFit: "contain", flexShrink: 0,
        filter: `drop-shadow(0 1px 4px ${color || "#b44fff"}66)`,
      }}
    />
  );
}

export default BeadShape;
